// Areas on maps (docs/tapestry-design.md §3.7): circles, rectangles (turned about their middle), cones and freeform
// shapes. Stored points are shares of the image's width and height; a circle's radius and a cone's reach are shares of
// its width. Here everything is worked out in the image's own pixels.

export type AreaShape = { shape: string; points: number[]; radius: number; angle: number; spread: number };
export type Point = [number, number];
type Grid = { kind: string; size: number };

const RAD = Math.PI / 180;

/** The area's outline as a polygon in image pixels (circles and a cone's arc as many short sides). */
export function outline(a: AreaShape, w: number, h: number): Point[] {
  const p = pairs(a.points, w, h);
  if (a.shape === "circle") {
    const [[cx, cy]] = p as [Point];
    const r = a.radius * w;
    return Array.from({ length: 72 }, (_, i): Point => [cx + r * Math.cos(i * 5 * RAD), cy + r * Math.sin(i * 5 * RAD)]);
  }
  if (a.shape === "rectangle") return rectangleCorners(a, w, h);
  if (a.shape === "cone") {
    const [[ax, ay]] = p as [Point];
    const r = a.radius * w;
    const steps = Math.max(2, Math.ceil(a.spread / 5));
    const start = a.angle - a.spread / 2;
    const arc = Array.from({ length: steps + 1 }, (_, i): Point => {
      const t = (start + (a.spread * i) / steps) * RAD;
      return [ax + r * Math.cos(t), ay + r * Math.sin(t)];
    });
    return [[ax, ay], ...arc];
  }
  return p;
}

/** A rectangle's four corners, turned by its angle about its middle. */
export function rectangleCorners(a: AreaShape, w: number, h: number): Point[] {
  const [[x1, y1], [x2, y2]] = pairs(a.points, w, h) as [Point, Point];
  const c: Point = [(x1 + x2) / 2, (y1 + y2) / 2];
  return ([[x1, y1], [x2, y1], [x2, y2], [x1, y2]] as Point[]).map((q) => turn(q, c, a.angle));
}

/** Where the area's handles are, in image pixels, by name. */
export function handles(a: AreaShape, w: number, h: number): Array<{ key: string; at: Point }> {
  const p = pairs(a.points, w, h);
  if (a.shape === "circle") return [{ key: "edge", at: [p[0]![0] + a.radius * w, p[0]![1]] }];
  if (a.shape === "rectangle") {
    const corners = rectangleCorners(a, w, h);
    return [{ key: "p0", at: corners[0]! }, { key: "p1", at: corners[2]! }];
  }
  if (a.shape === "cone") {
    const [ax, ay] = p[0]!;
    return [{ key: "apex", at: [ax, ay] }, { key: "tip", at: [ax + a.radius * w * Math.cos(a.angle * RAD), ay + a.radius * w * Math.sin(a.angle * RAD)] }];
  }
  return p.map((q, i) => ({ key: `v${i}`, at: q }));
}

/** The area with one handle dragged to (x, y) in image pixels. */
export function dragHandle(a: AreaShape, key: string, x: number, y: number, w: number, h: number): AreaShape {
  const p = pairs(a.points, w, h);
  const next = { ...a, points: [...a.points] };
  if (a.shape === "circle" && key === "edge") next.radius = Math.max(1, Math.hypot(x - p[0]![0], y - p[0]![1])) / w;
  else if (a.shape === "rectangle" && (key === "p0" || key === "p1")) {
    // In the rectangle's own (unturned) frame, about its middle.
    const c: Point = [(p[0]![0] + p[1]![0]) / 2, (p[0]![1] + p[1]![1]) / 2];
    const [ux, uy] = turn([x, y], c, -a.angle);
    const i = key === "p0" ? 0 : 2;
    next.points[i] = ux / w;
    next.points[i + 1] = uy / h;
  } else if (a.shape === "cone" && key === "apex") {
    next.points = [x / w, y / h];
  } else if (a.shape === "cone" && key === "tip") {
    const [ax, ay] = p[0]!;
    next.radius = Math.max(1, Math.hypot(x - ax, y - ay)) / w;
    next.angle = ((Math.atan2(y - ay, x - ax) / RAD) % 360 + 360) % 360;
  } else if (key.startsWith("v")) {
    const i = Number(key.slice(1));
    next.points[i * 2] = x / w;
    next.points[i * 2 + 1] = y / h;
  }
  return next;
}

/** The area moved by (dx, dy) image pixels. */
export function moveArea(a: AreaShape, dx: number, dy: number, w: number, h: number): AreaShape {
  return { ...a, points: a.points.map((v, i) => v + (i % 2 === 0 ? dx / w : dy / h)) };
}

/** The area's size in words: in grid cells when the map has a grid, otherwise in the image's pixels. */
export function sizeText(a: AreaShape, w: number, h: number, grid: Grid): string {
  const cell = grid.kind !== "none" && grid.size > 0 ? grid.size : 0;
  const n = (px: number): string => (cell ? `${round(px / cell)} cell${round(px / cell) === 1 ? "" : "s"}` : `${Math.round(px)} px`);
  if (a.shape === "circle") return `radius ${n(a.radius * w)}`;
  if (a.shape === "cone") return `${n(a.radius * w)} long, ${Math.round(a.spread)}° wide`;
  if (a.shape === "rectangle") {
    const [[x1, y1], [x2, y2]] = pairs(a.points, w, h) as [Point, Point];
    return `${n(Math.abs(x2 - x1))} × ${n(Math.abs(y2 - y1))}`;
  }
  return `${a.points.length / 2} corners`;
}

/** The middle of the area (for its label), in image pixels. */
export function middle(a: AreaShape, w: number, h: number): Point {
  if (a.shape === "circle") return pairs(a.points, w, h)[0]!;
  const o = a.shape === "cone"
    ? (() => {
        const [ax, ay] = pairs(a.points, w, h)[0]!;
        const r = (a.radius * w) / 2;
        return [[ax + r * Math.cos(a.angle * RAD), ay + r * Math.sin(a.angle * RAD)]] as Point[];
      })()
    : outline(a, w, h);
  return [o.reduce((s, q) => s + q[0], 0) / o.length, o.reduce((s, q) => s + q[1], 0) / o.length];
}

function pairs(points: number[], w: number, h: number): Point[] {
  const result: Point[] = [];
  for (let i = 0; i + 1 < points.length; i += 2) result.push([points[i]! * w, points[i + 1]! * h]);
  return result;
}

function turn([x, y]: Point, [cx, cy]: Point, degrees: number): Point {
  const t = degrees * RAD;
  const [dx, dy] = [x - cx, y - cy];
  return [cx + dx * Math.cos(t) - dy * Math.sin(t), cy + dx * Math.sin(t) + dy * Math.cos(t)];
}

function round(v: number): number {
  return Math.round(v * 10) / 10;
}
